import { useEffect, useRef, useState } from "react";
import PitchAnimation from "./PitchAnimation";
import IntensityAnimation from "./IntensityAnimation";
import MusicNotationView from "./MusicNotationView";
import ColorCodedWordsView from "./ColorCodedWordsView";
import PitchIntensityAnimation from "./PitchIntensityAnimation";
import axios from "axios";
import { defaultColormap } from "./colormaps";

/**
 * The exercise audio is a short fragment WAV whose media timeline starts at 0.
 * Read that real media clock directly; do not subtract the absolute source
 * segment start and do not multiply by playbackRate.
 */
function useLiveFragmentAudioTime(audioRef, segment) {
  const [relativeTime, setRelativeTime] = useState(0);
  const lastTimeRef = useRef(null);

  useEffect(() => {
    let rafId = null;

    const segmentStart = Number(segment?.start);
    const segmentEnd = Number(segment?.end);
    const segmentDuration =
      Number.isFinite(segmentStart) && Number.isFinite(segmentEnd)
        ? Math.max(0, segmentEnd - segmentStart)
        : null;

    const readRelativeTime = () => {
      const audio = audioRef?.current;

      if (!audio || !Number.isFinite(audio.currentTime)) {
        return 0;
      }

      const relative = Math.max(0, audio.currentTime);
      return segmentDuration == null
        ? relative
        : Math.min(segmentDuration, relative);
    };

    const update = () => {
      const next = readRelativeTime();
      const previous = lastTimeRef.current;
      lastTimeRef.current = next;

      if (previous == null || Math.abs(next - previous) > 0.0001) {
        setRelativeTime(next);
      }

      rafId = window.requestAnimationFrame(update);
    };

    const initial = readRelativeTime();
    lastTimeRef.current = initial;
    setRelativeTime(initial);
    rafId = window.requestAnimationFrame(update);

    return () => {
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [audioRef, segment?.start, segment?.end]);

  return relativeTime;
}

export default function NarratorIntonation({
  subject,
  file,
  segment,
  audioRef,
  view,
  speed,
  onWordClick,
}) {
  const [selectedColormap, setSelectedColormap] = useState(
    defaultColormap || "speechStable"
  );
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLegend, setShowLegend] = useState(true);

  // Exact, live replacement for the old broadcastTime snapshot.
  const liveRelativeTime = useLiveFragmentAudioTime(audioRef, segment);

  useEffect(() => {
    if (
      !subject ||
      !file?.file ||
      segment?.start == null ||
      segment?.end == null
    ) {
      setAssessment(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    let active = true;

    const encodePathPart = (value) =>
      encodeURIComponent(String(value ?? ""));

    const getIntonation = async () => {
      setLoading(true);
      const token = localStorage.getItem("token");

      const url =
        `${process.env.REACT_APP_USER_API_URL}/analysis/narrator/` +
        `${encodePathPart(subject.owner)}/` +
        `${encodePathPart(subject.language)}/` +
        `${encodePathPart(subject.author)}/` +
        `${encodePathPart(subject.title)}/` +
        `${encodePathPart(file.file)}/` +
        `${encodePathPart(segment.start)}/` +
        `${encodePathPart(segment.end)}/` +
        `${encodePathPart(subject.type)}`;

      try {
        const formData = new FormData();
        formData.append("text", segment?.text || "");

        const response = await axios.post(url, formData, {
          signal: controller.signal,
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {},
        });

        if (active) {
          setAssessment(response.data);
        }
      } catch (error) {
        if (
          error?.code === "ERR_CANCELED" ||
          error?.name === "CanceledError"
        ) {
          return;
        }

        console.error("Error loading intonation:", error);
        if (active) {
          setAssessment(null);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    getIntonation();

    return () => {
      active = false;
      controller.abort();
    };
  }, [
    subject?.owner,
    subject?.language,
    subject?.author,
    subject?.title,
    subject?.type,
    file?.file,
    segment?.start,
    segment?.end,
  ]);

  const narratorIntonation = assessment?.narrator_intonation;

  let intensityData;
  let pitchData;
  let pitch1stDerivativeData;

  if (narratorIntonation?.intensity?.intensity) {
    intensityData = narratorIntonation.intensity.intensity;
    pitchData = narratorIntonation.intensity.pitch;
    pitch1stDerivativeData =
      narratorIntonation.intensity.pitch_1st_derivative;
  } else {
    intensityData = narratorIntonation?.intensity;
    pitchData = narratorIntonation?.pitch;
    pitch1stDerivativeData = narratorIntonation?.pitch_1st_derivative;
  }

  const words = segment?.words || [];

  return (
    <div className="flex flex-col flex-1 space-y-3">
      {loading && <div>Loading intonation...</div>}

      {!loading && !assessment && <div>No intonation data available.</div>}

      {!loading && assessment && (
        <>
          {view === "pitch" && pitchData && (
            <PitchAnimation
              pitch={pitchData}
              segment={segment}
              audioRef={audioRef}
              onWordClick={onWordClick}
            />
          )}

          {view === "intensity" && intensityData && (
            <IntensityAnimation
              intensity={intensityData}
              segment={segment}
              audioRef={audioRef}
              broadcastTime={liveRelativeTime}
              onWordClick={onWordClick}
            />
          )}

          {view === "music" && pitchData && (
            <MusicNotationView
              pitch={pitchData}
              segment={segment}
              pitch1stDerivative={pitch1stDerivativeData}
              intensity={intensityData}
              audioRef={audioRef}
              broadcastTime={liveRelativeTime}
              showWordLabels={true}
            />
          )}

          {view === "colors" && pitchData && (
            <ColorCodedWordsView
              words={words}
              pitch={pitchData}
              intensity={intensityData}
              segment={segment}
              audioRef={audioRef}
              broadcastTime={liveRelativeTime}
              showLegend={showLegend}
              onToggleLegend={() => setShowLegend(!showLegend)}
            />
          )}

          {view === "combined" && intensityData && pitchData && (
            <PitchIntensityAnimation
              intensity={intensityData}
              pitch={pitchData}
              segment={segment}
              audioRef={audioRef}
              broadcastTime={liveRelativeTime}
              speed={speed}
              selectedColormap={selectedColormap}
              onColormapChange={setSelectedColormap}
              onWordClick={onWordClick}
            />
          )}
        </>
      )}
    </div>
  );
}